import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { StyledAutocomplete } from "../../styled/inputs/autocomplete";
import { getDrivers } from "../../../api/driver";

const ExtraDeliveryDialog = ({
  open,
  onClose,
  onConfirm,
  handleSelectChange,
  driverError,
  driverBlockedWarning,
  reason,
  setReason,
}) => {
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [reasonError, setReasonError] = useState("");

  useEffect(() => {
    if (!isAutocompleteOpen) {
      return;
    }
    loadDrivers();
  }, [isAutocompleteOpen]);

  const loadDrivers = async () => {
    try {
      setLoadingDrivers(true);
      if (!isAutocompleteOpen) return;

      const { data } = await getDrivers();
      data.sort((a, b) => a.name.localeCompare(b.name));

      const newOptions = data.map((driver) => ({
        value: driver.driver_id,
        label: `${driver.easy_id} - ${driver.name}`,
        firstLetter: /[0-9]/.test(driver.name[0]) ? "0-9" : driver.name[0],
        isBlocked: driver.has_active_shipping_route,
      }));

      setDrivers(newOptions);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const validateReason = (reasonValue) => {
    if (!reasonValue || reasonValue.trim().length === 0) {
      setReasonError("Motivo é obrigatório");
      return false;
    }
    if (reasonValue.trim().length < 3) {
      setReasonError("Motivo deve ter pelo menos 3 caracteres");
      return false;
    }
    setReasonError("");
    return true;
  };

  const handleReasonChange = (event) => {
    const newReason = event.target.value;
    setReason(newReason);

    if (reasonError) {
      validateReason(newReason);
    }
  };

  const handleConfirm = () => {
    const isReasonValid = validateReason(reason);

    if (!isReasonValid) {
      return;
    }

    onConfirm();
  };

  const isConfirmDisabled = () => {
    return !reason || reason.trim().length === 0 || !!driverError;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: "60vh",
        },
      }}
      sx={{
        zIndex: 9999999999,
      }}
    >
      <DialogTitle>Confirmação de Corrida Extra</DialogTitle>
      <DialogContent
        sx={{
          height: "calc(100% - 64px - 52px)",
          overflowY: "auto",
        }}
      >
        <FormControl error={!!driverError} sx={{ mb: 1, width: "100%" }}>
          <StyledAutocomplete
            disablePortal
            id="extra-delivery-select-driver-to-deliver-order"
            open={isAutocompleteOpen}
            onOpen={() => setIsAutocompleteOpen(true)}
            onClose={() => setIsAutocompleteOpen(false)}
            loading={loadingDrivers}
            options={drivers}
            groupBy={(option) => option.firstLetter}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Motorista"
                variant="outlined"
                error={!!driverError}
                helperText={driverError}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography>{option.label}</Typography>
                {option.isBlocked && (
                  <Chip
                    label="Adicionar à Corrida Atual"
                    color="info"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </li>
            )}
            onChange={(event, value) => {
              handleSelectChange(event, value);
            }}
            sx={{ mb: 1 }}
          />
        </FormControl>

        {driverBlockedWarning && (
          <Typography
            variant="body2"
            color="warning.main"
            sx={{ mt: 1, mb: 2 }}
          >
            {driverBlockedWarning}
          </Typography>
        )}

        <TextField
          label="Motivo *"
          variant="outlined"
          multiline
          rows={2}
          fullWidth
          required
          value={reason}
          onChange={handleReasonChange}
          onBlur={() => validateReason(reason)}
          error={!!reasonError}
          helperText={reasonError || "Campo obrigatório"}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          disabled={isConfirmDisabled()}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtraDeliveryDialog;
